import _ from 'underscore';
import { DefaultSortDirection } from '@bingads-webui-grid/campaign-details-list';
import { columnNames } from './strings';
import { buildColumn, formatFuncs } from './shared';
import { ColumnClicksWithFluctuation, ColumnImpressionsWithFluctuation, ColumnSpendWithFluctuation } from './fluctuation-columns';

const PerformanceMetricBaseColumnScope = 'PerformanceMetrics';
const PhonePerformanceMetricBaseColumnScope = 'PhonePerformanceMetrics';

export function buildPerformanceColumn(options = {}) {
  const {
    scope = PerformanceMetricBaseColumnScope, name, i18n, format, helpId, sortable,
  } = options;
  const key = options.key || options.name;
  const title = options.title || columnNames[key];
  const localizedTitle = i18n ? i18n.getString(title) : title;
  const property = options.property || `${scope}/${name}`;
  const onRenderPerformanceColumns = (item) => {
    const value = _.property([scope, name])(item);
    if (_.isUndefined(value) || _.isNull(value)) {
      return i18n ? i18n.getString(_TL_('-')) : '-';
    }
    if (format && typeof format === 'function') {
      return format(i18n)(value);
    }
    return value;
  };

  return {
    ...buildColumn({
      ...options, title: localizedTitle, helpId, key, sortable,
    }),
    defaultSortDirection: DefaultSortDirection.Descending,
    fieldName: property,
    onRenderAggregatedRow: onRenderPerformanceColumns,
    onRender: onRenderPerformanceColumns,
  };
}

export const ColumnClicks = ({ i18n, helpId = 'Page_CampaignGrids_Clicks' }) => buildPerformanceColumn({
  i18n,
  scope: PerformanceMetricBaseColumnScope,
  name: 'Clicks',
  format: formatFuncs.integer,
  helpId,
});

export const ColumnImpressions = ({ i18n, helpId = 'Page_CampaignGrids_Impr' }) => buildPerformanceColumn({
  i18n,
  scope: PerformanceMetricBaseColumnScope,
  name: 'Impressions',
  format: formatFuncs.integer,
  helpId,
});

export const ColumnCTR = ({ i18n, helpId = 'Page_CampaignGrids_CTR' }) => buildPerformanceColumn({
  i18n,
  scope: PerformanceMetricBaseColumnScope,
  name: 'CTR',
  format: formatFuncs.percentage,
  helpId,
});

export const ColumnAverageCPC = ({ i18n, helpId = 'Page_CampaignGrids_AvgCPC' }) => buildPerformanceColumn({
  i18n,
  scope: PerformanceMetricBaseColumnScope,
  name: 'AverageCPC',
  format: formatFuncs.decimal,
  helpId,
});

export const ColumnAveragePosition = ({ i18n, helpId = 'Page_CampaignGrids_AvgPosition' }) => buildPerformanceColumn({
  i18n,
  scope: PerformanceMetricBaseColumnScope,
  name: 'AveragePosition',
  format: formatFuncs.decimal,
  helpId,
});

export const ColumnAverageCPM = ({ i18n, helpId = 'Page_CampaignGrids_AvgCPM' }) => buildPerformanceColumn({
  i18n,
  scope: PerformanceMetricBaseColumnScope,
  name: 'AverageCPM',
  format: formatFuncs.decimal,
  helpId,
});

export const ColumnVideoViews = ({ i18n, helpId = 'Page_CampaignGrids_VideoViews' }) => buildPerformanceColumn({
  i18n,
  scope: PerformanceMetricBaseColumnScope,
  name: 'VideoViews',
  format: formatFuncs.integer,
  helpId,
});

export const ColumnViewThroughRate = ({ i18n, helpId = 'Page_CampaignGrids_ViewThroughRate' }) => buildPerformanceColumn({
  i18n,
  scope: PerformanceMetricBaseColumnScope,
  name: 'ViewThroughRate',
  format: formatFuncs.percentage,
  helpId,
});

export const ColumnAverageCPV = ({ i18n, helpId = 'Page_CampaignGrids_AverageCPV' }) => buildPerformanceColumn({
  i18n,
  scope: PerformanceMetricBaseColumnScope,
  name: 'AverageCPV',
  format: formatFuncs.decimal,
  helpId,
});

export const ColumnTotalWatchTimeInMS = ({ i18n, helpId = 'Page_CampaignGrids_TotalWatchTime' }) => buildPerformanceColumn({
  i18n,
  scope: PerformanceMetricBaseColumnScope,
  name: 'TotalWatchTimeInMS',
  format: formatFuncs.thousandth,
  helpId,
});

export const ColumnAverageWatchTimePerImpression = ({ i18n, helpId = 'Page_CampaignGrids_AverageWatchTimePerImpression' }) => buildPerformanceColumn({
  i18n,
  scope: PerformanceMetricBaseColumnScope,
  name: 'AverageWatchTimePerImpression',
  format: formatFuncs.decimal,
  helpId,
});

export const ColumnAverageWatchTimePerVideoView = ({ i18n, helpId = 'Page_CampaignGrids_AverageWatchTimePerVideoView' }) => buildPerformanceColumn({
  i18n,
  scope: PerformanceMetricBaseColumnScope,
  name: 'AverageWatchTimePerVideoView',
  format: formatFuncs.decimal,
  helpId,
});

export const ColumnVideoViewsAt25Percent = ({ i18n, helpId = 'Page_CampaignGrids_VideoViewsAt25Percent' }) => buildPerformanceColumn({
  i18n,
  scope: PerformanceMetricBaseColumnScope,
  name: 'VideoViewsAt25Percent',
  format: formatFuncs.integer,
  helpId,
});

export const ColumnVideoViewsAt50Percent = ({ i18n, helpId = 'Page_CampaignGrids_VideoViewsAt50Percent' }) => buildPerformanceColumn({
  i18n,
  scope: PerformanceMetricBaseColumnScope,
  name: 'VideoViewsAt50Percent',
  format: formatFuncs.integer,
  helpId,
});

export const ColumnVideoViewsAt75Percent = ({ i18n, helpId = 'Page_CampaignGrids_VideoViewsAt75Percent' }) => buildPerformanceColumn({
  i18n,
  scope: PerformanceMetricBaseColumnScope,
  name: 'VideoViewsAt75Percent',
  format: formatFuncs.integer,
  helpId,
});

export const ColumnCompletedVideoViews = ({ i18n, helpId = 'Page_CampaignGrids_CompletedVideoViews' }) => buildPerformanceColumn({
  i18n,
  scope: PerformanceMetricBaseColumnScope,
  name: 'CompletedVideoViews',
  format: formatFuncs.integer,
  helpId,
});

export const ColumnVideoCompletionRate = ({ i18n, helpId = 'Page_CampaignGrids_VideoCompletionRate' }) => buildPerformanceColumn({
  i18n,
  scope: PerformanceMetricBaseColumnScope,
  name: 'VideoCompletionRate',
  format: formatFuncs.percentage,
  helpId,
});

export const ColumnSpend = ({ i18n, helpId = 'Page_CampaignGrids_Spend' }) => buildPerformanceColumn({
  i18n,
  scope: PerformanceMetricBaseColumnScope,
  name: 'Spend',
  format: formatFuncs.decimal,
  helpId,
});

export const ColumnTotalSpend = ({ i18n, helpId }) => buildPerformanceColumn({
  i18n,
  scope: PhonePerformanceMetricBaseColumnScope,
  name: 'ClickAndPhoneSpend',
  format: formatFuncs.decimal,
  helpId,
});

export const ColumnDownloads = ({ i18n, helpId = 'pop_BA_AppAds_Downloads' }) => buildPerformanceColumn({
  i18n,
  scope: PerformanceMetricBaseColumnScope,
  name: 'Downloads',
  format: formatFuncs.integer,
  helpId,
});

export const ColumnFirstLaunches = ({ i18n, helpId = 'pop_BA_AppAds_Installs' }) => buildPerformanceColumn({
  i18n,
  scope: PerformanceMetricBaseColumnScope,
  name: 'FirstLaunches',
  format: formatFuncs.integer,
  helpId,
});

export const ColumnPurchases = ({ i18n, helpId = 'pop_BA_AppAds_InAppPurchase' }) => buildPerformanceColumn({
  i18n,
  scope: PerformanceMetricBaseColumnScope,
  name: 'Purchases',
  format: formatFuncs.integer,
  helpId,
});

export const ColumnSubscriptions = ({ i18n, helpId = 'pop_BA_AppAds_InAppSubscription' }) => buildPerformanceColumn({
  i18n,
  scope: PerformanceMetricBaseColumnScope,
  name: 'Subscriptions',
  format: formatFuncs.integer,
  helpId,
});

export const ColumnPostClickDownloadRate = ({ i18n, helpId = 'pop_BA_AppAds_DownloadRate' }) => buildPerformanceColumn({
  i18n,
  scope: PerformanceMetricBaseColumnScope,
  name: 'PostClickDownloadRate',
  format: formatFuncs.percentage,
  helpId,
});

export const ColumnCostPerDownload = ({ i18n, helpId = 'pop_BA_AppAds_CostPerDownload' }) => buildPerformanceColumn({
  i18n,
  scope: PerformanceMetricBaseColumnScope,
  name: 'CostPerDownload',
  format: formatFuncs.decimal,
  helpId,
});

export const ColumnPostClickFirstLaunchRate = ({ i18n, helpId = 'pop_BA_AppAds_InstallRate' }) => buildPerformanceColumn({
  i18n,
  scope: PerformanceMetricBaseColumnScope,
  name: 'PostClickFirstLaunchRate',
  format: formatFuncs.percentage,
  helpId,
});

export const ColumnCostPerFirstLaunch = ({ i18n, helpId = 'pop_BA_AppAds_CostPerInstall' }) => buildPerformanceColumn({
  i18n,
  scope: PerformanceMetricBaseColumnScope,
  name: 'CostPerFirstLaunch',
  format: formatFuncs.decimal,
  helpId,
});

export const ColumnPostInstallPurchaseRate = ({ i18n, helpId = 'pop_BA_AppAds_PostInstallPurchase' }) => buildPerformanceColumn({
  i18n,
  scope: PerformanceMetricBaseColumnScope,
  name: 'PostInstallPurchaseRate',
  format: formatFuncs.percentage,
  helpId,
});

export const ColumnCostPerPurchase = ({ i18n, helpId = 'pop_BA_AppAds_CostPerInAppPurchase' }) => buildPerformanceColumn({
  i18n,
  scope: PerformanceMetricBaseColumnScope,
  name: 'CostPerPurchase',
  format: formatFuncs.decimal,
  helpId,
});

export const ColumnPostInstallSubscriptionRate = ({ i18n, helpId = 'pop_BA_AppAds_PostInstallSubscription' }) => buildPerformanceColumn({
  i18n,
  scope: PerformanceMetricBaseColumnScope,
  name: 'PostInstallSubscriptionRate',
  format: formatFuncs.percentage,
  helpId,
});

export const ColumnCostPerSubscription = ({ i18n, helpId = 'pop_BA_AppAds_CostPerInAppSubscription' }) => buildPerformanceColumn({
  i18n,
  scope: PerformanceMetricBaseColumnScope,
  name: 'CostPerSubscription',
  format: formatFuncs.decimal,
  helpId,
});

export const ColumnTopImpressionRate = ({ i18n, helpId = 'pop_BA_Reporting_Clarity_TopImpressionRate' }) => buildPerformanceColumn({
  i18n,
  scope: PerformanceMetricBaseColumnScope,
  name: 'TopImpressionRate',
  format: formatFuncs.percentage,
  helpId,
});

export const ColumnAbsoluteTopImpressionRate = ({ i18n, helpId = 'pop_BA_Reporting_Clarity_AbsoluteTopImpressionRate' }) => buildPerformanceColumn({
  i18n,
  scope: PerformanceMetricBaseColumnScope,
  name: 'AbsoluteTopImpressionRate',
  format: formatFuncs.percentage,
  helpId,
});

export const ColumnAverageCPS = ({ i18n, helpId = 'pop_BA_CampaignGrids_CostPerSale' }) => buildPerformanceColumn({
  i18n,
  name: 'AverageCPS',
  format: formatFuncs.decimal,
  helpId,
});

export const ColumnWebsiteCoverage = ({ i18n, helpId = 'pop_BA_PMAX_WebsiteCoverage' }) => buildPerformanceColumn({
  i18n,
  name: 'WebsiteCoverage',
  format: formatFuncs.percentage,
  helpId,
});

export const getPerformanceColumns = ({
  withFluctuations,
  IsVideoAdsPerfMetricsEnabled,
} = {}) =>
  _.compact([
    withFluctuations ? ColumnClicksWithFluctuation : ColumnClicks,
    withFluctuations ? ColumnImpressionsWithFluctuation : ColumnImpressions,
    ColumnCTR,
    ColumnAverageCPC,
    withFluctuations ? ColumnSpendWithFluctuation : ColumnSpend,
    ColumnAveragePosition,
    ColumnAverageCPM,
    IsVideoAdsPerfMetricsEnabled && ColumnVideoViews,
    IsVideoAdsPerfMetricsEnabled && ColumnViewThroughRate,
    IsVideoAdsPerfMetricsEnabled && ColumnAverageCPV,
    IsVideoAdsPerfMetricsEnabled && ColumnTotalWatchTimeInMS,
    IsVideoAdsPerfMetricsEnabled && ColumnAverageWatchTimePerImpression,
    IsVideoAdsPerfMetricsEnabled && ColumnAverageWatchTimePerVideoView,
    IsVideoAdsPerfMetricsEnabled && ColumnVideoViewsAt25Percent,
    IsVideoAdsPerfMetricsEnabled && ColumnVideoViewsAt50Percent,
    IsVideoAdsPerfMetricsEnabled && ColumnVideoViewsAt75Percent,
    IsVideoAdsPerfMetricsEnabled && ColumnCompletedVideoViews,
    IsVideoAdsPerfMetricsEnabled && ColumnVideoCompletionRate,
    ColumnTotalSpend,
  ]);
